import { FC, useEffect, useMemo, useState } from 'react';

import CurrencyInputStyled from 'src/components/inputs/NPICurrencyInput/NPICurrencyInput.styles';
import { NPICurrencyInputProps } from 'src/components/inputs/NPICurrencyInput/NPICurrencyInput.types';
import {
    getDefaultValue,
    getInitialCurrencyValue,
    parseCurrencyInput,
} from 'src/components/inputs/NPICurrencyInput/NPICurrencyInput.util';
import { showErrorToast } from 'src/components/Toasts/toast';
import useConvertCurrencyToCentsDebounced from 'src/hooks/useConvertCurrencyToCentsDebounced/useConvertCurrencyToCentsDebounced';
import { useCurrency } from 'src/hooks/useCurrency';
import { currencyStringHasMoreThanTwoDecimals } from 'src/utils/locales/locale-to-currency';

export const defaultStepIncrementAmount = 0.01;
const MAX_DECIMALS_ERROR = 'Only 2 values are allowed after the decimal point';

/**
 * Currency input with robust validation, formatting, and debounced conversion to cents.
 * Handles 'k' shorthand, always formats to 2 decimals, and prevents >2 decimals.
 */
export const NPICurrencyInput: FC<NPICurrencyInputProps> = ({
    name,
    dataTestId = name ? `${name}-currency-input` : 'currency-input',
    handleValue,
    error,
    defaultValueInCents,
    defaultValueInCurrency,
    onBlur,
    sx,
    min,
    max,
    touched,
    errorMessage,
    setErrorMessage,
    country,
    ...props
}) => {
    const { currencySymbol } = useCurrency(country);

    const initialValue = useMemo(() => {
        return getInitialCurrencyValue({ defaultValueInCents, defaultValueInCurrency });
    }, [defaultValueInCents, defaultValueInCurrency]);

    const [amountInCurrency, setAmountInCurrency] = useState<string>(initialValue);
    const { defaultValue: defaultValueProp, ...restProps } = props;
    const defaultValueInitial = useMemo(() => {
        return getDefaultValue({
            defaultValue: defaultValueInCurrency ?? defaultValueProp,
            defaultValueInCents,
        });
    }, [defaultValueInCents, defaultValueInCurrency, defaultValueProp]);

    const [defaultValue, setDefaultValue] = useState<string>(defaultValueInitial);

    useEffect(() => {
        setDefaultValue(defaultValueInitial);
    }, [defaultValueInitial]);

    // Only set the amount currency at initialization, not on every initialValue change
    // This allows manual decimal input without it being overwritten
    useEffect(() => {
        // Only update if component is mounting (not on re-renders)
        setAmountInCurrency(initialValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useConvertCurrencyToCentsDebounced({
        amountInCurrency: Number.parseFloat(amountInCurrency) || 0,
        handleValue,
        min,
        max,
        errorMessage,
        setErrorMessage,
        touched,
    });

    function handleValueChange(inputValue: string) {
        const parsed = parseCurrencyInput(inputValue);

        // Allow empty string for controlled input
        if (parsed === '') {
            setAmountInCurrency('');
            return;
        }
        // Prevent more than 2 decimals but allow input ending with decimal point
        if (/^\d*(\.\d{0,2})?$/.test(parsed) || /^\d*\.$/.test(parsed)) {
            setAmountInCurrency(parsed);
        } else if (/\.\d{3,}/.test(parsed)) {
            showErrorToast(MAX_DECIMALS_ERROR);
        }
    }

    function handleInputBlur(event: React.FocusEvent<HTMLInputElement>) {
        if (onBlur) {
            onBlur(event);
        }
        let formatted = amountInCurrency;
        if (formatted === '' || Number.isNaN(Number(formatted))) {
            formatted = '0.00';
        } else {
            // Check if the value ends with a decimal point (e.g., "5.")
            if (formatted.endsWith('.')) {
                // Add zeros when there's only a decimal point (e.g., "5." becomes "5.00")
                const formattedWithZero = `${formatted}0`;
                formatted = Number.parseFloat(formattedWithZero).toFixed(2);
            } else {
                // Normal formatting for numbers without trailing decimal point
                formatted = Number.parseFloat(formatted).toFixed(2);
            }
        }
        setAmountInCurrency(formatted);
    }

    return (
        <CurrencyInputStyled
            name={name}
            defaultValue={defaultValue}
            value={amountInCurrency}
            decimalsLimit={2}
            decimalScale={2}
            allowDecimals
            allowNegativeValue={false}
            // Let our custom handleInputBlur handle the formatting instead
            // This gives us more control over when and how to format
            formatValueOnBlur={false}
            onValueChange={(value, _name, values) => {
                handleValueChange(values.value);
            }}
            placeholder={`${currencySymbol}`}
            onBlur={handleInputBlur}
            step={defaultStepIncrementAmount}
            prefix={currencySymbol}
            error={error}
            data-testid={dataTestId}
            sx={sx}
            {...restProps}
            onChange={(event) => {
                const { value } = event.target;

                // Allow decimal point at the end without showing error
                const isTypingDecimal = value.endsWith('.') || value.endsWith(',');

                if (!isTypingDecimal && currencyStringHasMoreThanTwoDecimals(value)) {
                    showErrorToast(MAX_DECIMALS_ERROR);
                }
            }}
        />
    );
};

export default NPICurrencyInput;
